import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp'
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion'
import MuiAccordionSummary, { AccordionSummaryProps } from '@mui/material/AccordionSummary'
import MuiAccordionDetails from '@mui/material/AccordionDetails'
import {
  Divider,
  Drawer,
  Grid,
  ListItemButton,
  Stack,
  Tooltip,
  Typography,
  styled,
  useTheme,
} from '@mui/material'
import { HelpOutlineRounded, AddRounded } from '@mui/icons-material'
import ArrowUpIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownIcon from '@mui/icons-material/ArrowDownward'
import CloseIcon from '@mui/icons-material/Close'
import { Chart } from 'react-chartjs-2'
import { GapCause, GapExtended } from '../../shared/model/models'
import { DiversityDimenstionConfig, ModuleConfig } from '../../shared/model'
import FsDetailDescription from '../fs-gap-descriptive/fs-gap-descriptive'
import { WALKTHROUGH_STATUS, fsColors } from '../../shared/constants'
import { translatedDomainName } from '../../shared/utils'
import { changeWalkthroughIndex, changeWalkthroughStatus } from '../../store/actions'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import FsLeftInfoDrawer from '../common/atoms/LeftInfoDrawer'

type FsGapCauseProps = {
  gap: GapExtended
  moduleConfig?: ModuleConfig
  diversityDimenstionConfig?: DiversityDimenstionConfig
}

export interface GraphSeries {
  name: string
  type: string
  data: string[]
  dataLabel: string[]
  id: number
}

const FsGapCause = (props: FsGapCauseProps) => {
  const dispatch = useAppDispatch()
  const { walkthroughStatus, walkthroughIndex, currentTheme } = useAppSelector(
    (state) => state.currentUser,
  )

  const { gap, moduleConfig, diversityDimenstionConfig } = props
  const { causes, id: gapId, adjusted } = gap
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const posGapGraphSeries: GraphSeries[] = []
  const neGapGraphSeries: GraphSeries[] = []
  let posGapValue = 0
  let neGapValue = 0
  const [gapDescriptionsId, setGapDescriptionsId] = useState<number>()
  const [explainOpen, setExplainOpen] = useState<boolean>(false)
  const [open, setOpen] = useState(false)
  const handleOpen = () => setOpen(true)
  const handleClose = () => {
    if (walkthroughStatus === WALKTHROUGH_STATUS.InProgress) {
      dispatch(changeWalkthroughStatus(WALKTHROUGH_STATUS.Paused))
    }
    setOpen(false)
  }

  const [helpOpen, setHelpOpen] = useState(false)
  const handleHelpOpen = () => setHelpOpen(true)
  const handleHelpClose = () => setHelpOpen(false)

  const gapCauses = [...causes].sort((a, b) => Number(b.explainedPart) - Number(a.explainedPart))
  gapCauses.forEach((cause: GapCause) => {
    /* exclude constant effect (by convention name=constant) for explained part only, because its by convention always zero */
    if (cause.empiricalConcept.name !== 'constant') {
      const gs = {
        name: t(`empiricalConcept.name.${cause.empiricalConcept.name}` as any),
        type: 'bar',
        data: [Number(cause.explainedPart).toFixed(3)],
        dataLabel: [
          Number(cause.explainedPart).toLocaleString(i18n.language, {
            maximumFractionDigits: 3,
            minimumFractionDigits: 3,
          }),
        ],
        id: cause.empiricalConcept.id,
        label: {
          show: true,
        },
      }
      if (Math.round((Number(cause.explainedPart) + Number.EPSILON) * 1000) / 1000 >= 0) {
        posGapGraphSeries.push(gs)
        posGapValue += Math.round((Number(cause.explainedPart) + Number.EPSILON) * 1000) / 1000
      } else {
        neGapGraphSeries.push(gs)
      }
    }
  })

  const unadjusted = Math.round((Number(gap?.unadjusted ?? 0) + Number.EPSILON) * 100) / 100
  const aadjusted = Math.round((Number(gap?.adjusted ?? 0) + Number.EPSILON) * 100) / 100
  const explained = Math.round((Number(unadjusted - adjusted) + Number.EPSILON) * 100) / 100
  posGapValue = Math.round((Number(posGapValue) + Number.EPSILON) * 100) / 100
  neGapValue = Math.round((Number(explained - posGapValue) + Number.EPSILON) * 100) / 100

  const gapGraphSeries = [...posGapGraphSeries, ...neGapGraphSeries]

  const loadDetails = (empiricalConceptId: number) => {
    setGapDescriptionsId(empiricalConceptId)
    if (walkthroughStatus === WALKTHROUGH_STATUS.InProgress) {
      dispatch(changeWalkthroughStatus(WALKTHROUGH_STATUS.Paused))
    }
    handleOpen()
  }

  useEffect(() => {
    if (!open && walkthroughStatus === WALKTHROUGH_STATUS.Paused && walkthroughIndex === 8) {
      dispatch(changeWalkthroughStatus(WALKTHROUGH_STATUS.InProgress))
      dispatch(changeWalkthroughIndex(9))
    }
  }, [open])

  const onCauseClick = (cause: GraphSeries) => {
    const empiricalConceptId = gapGraphSeries.find((it: GraphSeries) => it.name === cause.name)!.id
    loadDetails(empiricalConceptId)
  }

  const onEvents = {
    onClick: (_: any, value: any) => {
      if (value.length > 0) {
        loadDetails(value[0].element.$context.raw.id)
      }
    },
    onHover: (event: any, value: any) => {
      event.native.target.style.cursor = value.length > 0 ? 'pointer' : 'default'
    },
  }

  const helpTextHtml =
    gap && gap.gapResultDescriptions.filter((desc) => desc.locale === i18n.language)
      ? gap.gapResultDescriptions.filter((desc) => desc.locale === i18n.language)[0].gapCause
      : ''

  const posGraphColors = diversityDimenstionConfig?.fsColor?.colors
    ? [...diversityDimenstionConfig.fsColor.colors.slice(0, 3).map((c) => `${c}`)]
    : []

  posGapGraphSeries.slice(3).forEach(() => posGraphColors.push(theme.palette.divider))
  const ngGraphColors = diversityDimenstionConfig?.fsColor?.colors
    ? [...diversityDimenstionConfig.fsColor.colors.slice(0, 3).map((c) => `${c}`)]
    : []

  neGapGraphSeries.slice(3).forEach(() => ngGraphColors.push(`${theme.palette.divider}`))
  ngGraphColors.reverse()
  const graphCauses = [...posGapGraphSeries, ...neGapGraphSeries]

  const referenceGroupCount = Math.round(
    gap?.description.referenceGroup.descriptionDependentVariable.numberOfObservations,
  )
  const groupOfInterestCount = Math.round(
    gap?.description.groupOfInterest.descriptionDependentVariable.numberOfObservations,
  )
  const groupOfInterestPercentage =
    (groupOfInterestCount * 100) / (referenceGroupCount + groupOfInterestCount)

  const Accordion = styled((aprops: AccordionProps) => (
    <MuiAccordion disableGutters elevation={0} square {...aprops} />
  ))(() => ({
    border: `1px solid ${fsColors.fsGray.colors[1]}`,
    borderRadius: 8,
    marginBottom: 16,
  }))

  const AccordionSummary = styled((aprops: AccordionSummaryProps) => (
    <MuiAccordionSummary
      expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '1rem' }} />}
      {...aprops}
    />
  ))(() => ({
    backgroundColor: theme.palette.action.hover,
    flexDirection: 'row-reverse',
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
      transform: 'rotate(90deg)',
    },
    '& .MuiAccordionSummary-content': {
      marginLeft: theme.spacing(1),
    },
  }))

  const AccordionDetails = styled(MuiAccordionDetails)(() => ({
    padding: theme.spacing(4),
    borderTop: `1px solid ${fsColors.fsGray.colors[1]}`,
  }))

  return (
    <Box>
      {adjusted ? (
        <>
          <Accordion
            expanded={explainOpen}
            onChange={(_, isExpanded) => setExplainOpen(isExpanded)}
          >
            <AccordionSummary>
              <Stack>
                <Typography>{t('detailed.desc.causes.title')}</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                <Stack direction={{ sm: 'column', md: 'column', lg: 'row' }}>
                  <Box width={{ sm: '100%', md: '100%', lg: '60%' }} pr={{ sm: 0, md: 0, lg: 6 }}>
                    <Stack direction="row" alignItems="center" justifyContent="center">
                      <Typography variant="body1" textAlign="center">
                        (
                        {Number(gap.unadjustedRegression).toLocaleString(i18n.language, {
                          maximumFractionDigits: 2,
                          minimumFractionDigits: 2,
                        })}{' '}
                        %)
                      </Typography>
                      {Number(gap.unadjustedRegression).toLocaleString(i18n.language, {
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                      }) !==
                        Number(unadjusted).toLocaleString(i18n.language, {
                          maximumFractionDigits: 2,
                          minimumFractionDigits: 2,
                        }) && (
                        <Tooltip
                          title={
                            <Box p={1}>
                              <Typography my={2} component="div" textAlign="justify">
                                {i18n.language === 'en' ? (
                                  <>
                                    <Typography display="inline">The</Typography>{' '}
                                    <Typography className="font-semibold" display="inline">
                                      Unadjusted {t(`${moduleConfig?.module}_gap` as any)}
                                    </Typography>
                                  </>
                                ) : (
                                  <>
                                    <Typography display="inline">Der hier berechnete</Typography>{' '}
                                    <Typography className="font-semibold" display="inline">
                                      Unbereinigte {t(`${moduleConfig?.module}_gap` as any)}
                                    </Typography>
                                  </>
                                )}{' '}
                                <Typography display="inline">
                                  {t('detailed.desc.causes.tooltip', {
                                    goi_number: Number(
                                      gap.description.groupOfInterest.observationsRegression,
                                    ).toLocaleString(i18n.language, {
                                      minimumFractionDigits: 0,
                                      maximumFractionDigits: 0,
                                    }),
                                    goi_label: translatedDomainName(
                                      `${gap?.description.groupOfInterest.groupVariable.label}`,
                                      t,
                                    ),
                                    rg_number: Number(
                                      gap.description.referenceGroup.observationsRegression,
                                    ).toLocaleString(i18n.language, {
                                      minimumFractionDigits: 0,
                                      maximumFractionDigits: 0,
                                    }),
                                    rg_label: translatedDomainName(
                                      `${gap?.description.referenceGroup.groupVariable.label}`,
                                      t,
                                    ),
                                    module: t(`${moduleConfig?.module}_gap` as any),
                                    diff: Number(
                                      Math.abs(gap.unadjustedRegression - gap.unadjusted),
                                    ).toLocaleString(i18n.language, {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    }),
                                  })}
                                </Typography>
                              </Typography>
                            </Box>
                          }
                          placement="right"
                          arrow
                          enterTouchDelay={0}
                        >
                          <HelpOutlineRounded
                            fontSize="small"
                            sx={{ ml: 1, color: fsColors.fsGray.colors[1] }}
                          />
                        </Tooltip>
                      )}
                    </Stack>
                    <Typography variant="h3" className="mb-1" textAlign="center">
                      {t('unadjusteder')} {t(`${moduleConfig?.module}_gap` as any)}
                    </Typography>
                    <Box
                      sx={{
                        width: '100%',
                        height: '10px',
                        border: `2px solid ${theme.palette.text.primary}`,
                        borderBottom: 0,
                        borderRadius: 1,
                        borderBottomLeftRadius: 0,
                        borderBottomRightRadius: 0,
                        marginTop: 1,
                      }}
                    />
                    <Stack direction="row" alignItems="flex-start" justifyContent="center" my={1}>
                      <div style={{ width: '50%' }}>
                        <div
                          style={{
                            backgroundColor: moduleConfig?.fsColor.light,
                            height: '20px',
                            width: '100%',
                            borderRadius: 8,
                          }}
                        />
                        <Box
                          sx={{
                            width: '100%',
                            height: '10px',
                            border: `2px solid ${moduleConfig?.fsColor.primary}`,
                            borderTop: 0,
                            borderRadius: 1,
                            borderTopLeftRadius: 0,
                            borderTopRightRadius: 0,
                            marginY: 1,
                          }}
                        />
                        <Typography variant="h3" textAlign="center">
                          {t('adjusteder')} {t(`${moduleConfig?.module}_gap` as any)}
                        </Typography>
                        <Typography variant="body1" textAlign="center" sx={{ px: 1 }}>
                          (
                          {Number(aadjusted).toLocaleString(i18n.language, {
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 2,
                          })}{' '}
                          %)
                        </Typography>
                      </div>
                      <Box className="w-4 rounded-full h-4 mt-0.5 mx-1 flex items-center justify-center">
                        <AddRounded sx={{ fontSize: 16 }} />
                      </Box>
                      <div style={{ width: '50%' }}>
                        <Stack direction="row" height="20px">
                          <div
                            style={{
                              backgroundColor: diversityDimenstionConfig?.fsColor.primary,
                              width: '10%',
                              borderTopLeftRadius: 8,
                              borderBottomLeftRadius: 8,
                            }}
                          />
                          <div
                            style={{
                              backgroundColor: diversityDimenstionConfig?.fsColor.light,
                              width: '5%',
                            }}
                          />
                          <div
                            style={{
                              backgroundColor: diversityDimenstionConfig?.fsColor.colors[2],
                              width: '2.5%',
                            }}
                          />
                          <div
                            style={{
                              backgroundColor: theme.palette.divider,
                              width: '10%',
                            }}
                          />
                          <div
                            style={{
                              backgroundColor: diversityDimenstionConfig?.fsColor.colors[2],
                              width: '2.5%',
                            }}
                          />
                          <div
                            style={{
                              backgroundColor: diversityDimenstionConfig?.fsColor.light,
                              width: '20%',
                            }}
                          />
                          <div
                            style={{
                              backgroundColor: diversityDimenstionConfig?.fsColor.primary,
                              width: '50%',
                              borderTopRightRadius: 8,
                              borderBottomRightRadius: 8,
                            }}
                          />
                        </Stack>
                        <Box
                          sx={{
                            width: '100%',
                            height: '10px',
                            border: `2px solid ${diversityDimenstionConfig?.fsColor.primary}`,
                            borderTop: 0,
                            borderRadius: 1,
                            borderTopLeftRadius: 0,
                            borderTopRightRadius: 0,
                            marginY: 1,
                          }}
                        />
                        <Typography variant="h3" textAlign="center">
                          {t('gap_causes')}
                        </Typography>
                        <Typography variant="body1" textAlign="center" sx={{ px: 1 }}>
                          (
                          {Number(explained).toLocaleString(i18n.language, {
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 2,
                          })}{' '}
                          %)
                        </Typography>
                      </div>
                    </Stack>
                    <Typography variant="body2" className="font-semibold mt-5">
                      {t('detailed.desc.causes.graph.illus')}
                    </Typography>
                  </Box>
                  <Box width={{ sm: '100%', md: '100%', lg: '40%' }} pt={{ sm: 4, md: 4, lg: 0 }}>
                    <Typography variant="h3">{t('gap_causes')}</Typography>
                    <Typography textAlign="justify" my={2}>
                      {t('detailed.desc.causes.section', {
                        diff: Number(explained).toLocaleString(i18n.language, {
                          maximumFractionDigits: 2,
                          minimumFractionDigits: 2,
                        }),
                        adjusted: Number(aadjusted).toLocaleString(i18n.language, {
                          maximumFractionDigits: 2,
                          minimumFractionDigits: 2,
                        }),
                      })}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </AccordionDetails>
          </Accordion>
          <Stack className="items-center w-full">
            <Typography variant="body2" className="font-semibold mb-3">
              {adjusted ? t('overview.bar.chart.description') : ''}
            </Typography>
            <Typography component="div" className="flex items-end">
              {t('explain.gap.causes', {
                gap: t('gap'),
                number: Number(explained).toLocaleString(i18n.language, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }),
              })}
            </Typography>
            <Box height={16} border={`1px solid ${diversityDimenstionConfig?.fsColor.primary}`} />
            <Box
              width="100%"
              height={6}
              borderRadius={1}
              border={`2px solid ${diversityDimenstionConfig?.fsColor.primary}`}
              sx={{ borderBottom: 0, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
            />
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="center" pb={4} mt={1}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                width: '30%',
                minWidth: { sm: 200, md: 275, lg: 300 },
              }}
            >
              <ArrowUpIcon fontSize="small" />
              <Typography className="ml-1" mt={-0.25}>
                {t('explain.negative.causes', {
                  gap: t('gap'),
                  number: Number(neGapValue).toLocaleString(i18n.language, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }),
                })}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'flex-end',
                width: '30%',
                minWidth: { sm: 200, md: 275, lg: 300 },
              }}
            >
              <Typography className="mr-1" mt={-0.25} textAlign="right">
                {t('explain.positive.causes', {
                  gap: t('gap'),
                  number: Number(posGapValue).toLocaleString(i18n.language, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }),
                })}
              </Typography>
              <ArrowDownIcon fontSize="small" />
            </Box>
          </Stack>
        </>
      ) : null}
      <Stack direction="row" justifyContent="space-between" alignItems="center" my={4}>
        <Box width="100%" paddingX={{ sm: 0, md: 8 }}>
          {!adjusted ? (
            <Box paddingRight={4}>
              <Typography variant="h3" color="gray">
                {t('gap.causes.empty')}
              </Typography>
              <Typography variant="h3" color="gray" mb={4}>
                {t('gap.causes.emptycaption')}
              </Typography>
              {gapGraphSeries.map((gs) => (
                <Stack
                  sx={{ cursor: 'pointer', maxWidth: 600 }}
                  onClick={() => onCauseClick(gs)}
                  key={gs.name}
                >
                  <ListItemButton>
                    <Grid container className="w-full">
                      <Grid size={9}>
                        <Typography variant="body2">{gs.name}</Typography>
                      </Grid>
                      <Grid size={3}>
                        <Typography
                          variant="body2"
                          style={{ textDecoration: 'underline' }}
                          textAlign="right"
                        >
                          {t('view.details')}
                        </Typography>
                      </Grid>
                    </Grid>
                  </ListItemButton>
                  <Divider
                    sx={{ borderColor: diversityDimenstionConfig?.fsColor?.colors[2] || 'primary' }}
                  />
                </Stack>
              ))}
            </Box>
          ) : (
            <div style={{ position: 'relative', zIndex: 100, minHeight: '550px' }}>
              <Chart
                type="bar"
                id="gap-causes-bar-chart"
                data={{
                  datasets: [
                    {
                      data: [...posGapGraphSeries, ...neGapGraphSeries],
                      backgroundColor: [...posGraphColors, ...ngGraphColors],
                      borderRadius: 4,
                      datalabels: {
                        labels: {
                          value: {
                            anchor: (context) =>
                              Number(graphCauses[context.dataIndex].data[0]) > 0 ? 'end' : 'start',
                            align: (context) =>
                              Number(graphCauses[context.dataIndex].data[0]) > 0 ? 'end' : 'start',
                            color: theme.palette.text.primary,
                            display: true,
                            font: (context) => {
                              const { width } = context.chart
                              const size = Math.round(width / 35)
                              return {
                                size: size < 16 ? size : 16,
                              }
                            },
                            formatter: (value) => (value?.data ? `${value.dataLabel[0]} %` : ''),
                          },
                          name: {
                            anchor: (context) =>
                              Number(graphCauses[context.dataIndex].data[0]) > 0 ? 'start' : 'end',
                            align: (context) =>
                              Number(graphCauses[context.dataIndex].data[0]) > 0 ? 'start' : 'end',
                            color: theme.palette.text.primary,
                            display: true,
                            formatter: (value) => (value ? value.name : ''),
                            font: (context) => {
                              const { width } = context.chart
                              const size = Math.round(width / 35)
                              const hoveredSize = Math.round(width / 32)
                              return {
                                // @ts-ignore
                                size: context.hovered
                                  ? hoveredSize < 18
                                    ? hoveredSize
                                    : 18
                                  : size < 16
                                  ? size
                                  : 16,
                              }
                            },
                            listeners: {
                              click: (context) => loadDetails(graphCauses[context.dataIndex].id),
                              enter: (context) => {
                                // @ts-ignore
                                context.hovered = true
                                context.chart.canvas.style.cursor = 'pointer'
                                return true
                              },
                              leave: (context) => {
                                // @ts-ignore
                                context.hovered = false
                                context.chart.canvas.style.cursor = 'default'
                                return true
                              },
                            },
                          },
                        },
                      },
                    },
                  ],
                }}
                options={{
                  maintainAspectRatio: false,
                  parsing: {
                    xAxisKey: 'data.0',
                    yAxisKey: 'name',
                  },
                  indexAxis: 'y',
                  layout: {
                    padding: {
                      left: 70,
                      right: 70,
                    },
                  },
                  plugins: {
                    // @ts-ignore
                    legendMargin: false,
                    legend: {
                      display: false,
                    },
                    tooltip: {
                      enabled: false,
                      mode: 'dataset',
                    },
                  },
                  scales: {
                    x: {
                      grid: {
                        display: true,
                        color: theme.palette.divider,
                      },
                      border: {
                        color: theme.palette.text.disabled,
                      },
                      ticks: {
                        autoSkip: true,
                        maxTicksLimit: 15,
                        callback: (value) =>
                          `${value.toLocaleString(i18n.language, {
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 2,
                          })} %`,
                        color: theme.palette.text.primary,
                      },
                    },
                    y: {
                      grid: {
                        display: false,
                      },
                      border: {
                        display: false,
                      },
                      ticks: {
                        display: false,
                      },
                    },
                  },
                  events: ['click', 'mousemove'],
                  interaction: {
                    mode: 'point',
                  },
                  ...onEvents,
                }}
              />
            </div>
          )}
        </Box>
      </Stack>
      <Drawer
        anchor="bottom"
        open={open}
        onClose={handleClose}
        ModalProps={{
          keepMounted: false,
        }}
        slotProps={{
          paper: {
            sx: {
              height: 'calc(100% - 74px)',
              top: 74,
              marginX: 0,
              borderTopRightRadius: 8,
              borderTopLeftRadius: 8,
              padding: 4,
              elevation: 0,
              paddingBottom: 8,
              backgroundColor: currentTheme === 'light' ? theme.palette.grey[50] : '',
            },
          },
        }}
        variant="temporary"
        elevation={0}
        onTransitionEnd={() => {
          if (open && walkthroughStatus === WALKTHROUGH_STATUS.Paused && walkthroughIndex === 6) {
            dispatch(changeWalkthroughStatus(WALKTHROUGH_STATUS.InProgress))
            dispatch(changeWalkthroughIndex(7))
          }
        }}
      >
        <Box
          onClick={handleClose}
          display="flex"
          justifyContent="flex-end"
          sx={{
            cursor: 'pointer',
            width: 'fit-content',
            alignSelf: 'flex-end',
            zIndex: 50,
            mt: -1,
          }}
        >
          <CloseIcon />
        </Box>
        <FsDetailDescription
          gapId={gapId}
          empiricalConceptId={gapDescriptionsId ?? 0}
          moduleConfig={moduleConfig}
          diversityDimenstionConfig={diversityDimenstionConfig}
          gapGraphSeries={gapGraphSeries}
          groupOfInterestPercentage={groupOfInterestPercentage}
        />
      </Drawer>
      <FsLeftInfoDrawer open={helpOpen} onClose={handleHelpClose}>
        <Typography component="div" style={{ whiteSpace: 'pre-line' }}>
          {helpTextHtml}
        </Typography>
      </FsLeftInfoDrawer>
    </Box>
  )
}

export default FsGapCause
